import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Star, Languages } from "lucide-react";
import { scrollToId } from "../../lib/scroll";
import { useLang } from "../../i18n/LanguageContext";

export const Nav = () => {
  const { t, toggle } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const links = t.nav.links;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    scrollToId(href);
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      data-testid="main-nav"
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-500 ${
        scrolled ? "glass shadow-lg" : "bg-ink-800/40 backdrop-blur-sm"
      }`}
    >
      <nav className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 md:px-8">
        <button onClick={() => go("#top")} data-testid="nav-logo" className="group flex items-center gap-2.5">
          <span className="relative flex h-9 w-9 items-center justify-center rounded-full border border-gold-light/40">
            <Star className="h-4 w-4 text-gold-light" fill="currentColor" />
          </span>
          <span className="text-left leading-tight">
            <span className="block font-display text-xl font-semibold tracking-wide text-white">
              Sapiens <span className="text-gold-gradient">Celestia</span>
            </span>
            <span className="block text-[10px] uppercase tracking-[0.28em] text-gold-light/70">
              {t.hero.runway}
            </span>
          </span>
        </button>

        <div className="hidden items-center gap-8 lg:flex">
          {links.slice(0, -1).map((l) => (
            <button
              key={l.href}
              onClick={() => go(l.href)}
              data-testid={`nav-link-${l.key}`}
              className="text-sm font-light tracking-wide text-white/70 transition-colors hover:text-gold-light"
            >
              {l.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2.5">
          <button
            onClick={toggle}
            data-testid="lang-toggle"
            className="flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-2 text-xs font-medium text-white/80 transition-colors hover:border-gold-light/50 hover:text-gold-light"
          >
            <Languages className="h-4 w-4" />
            {t.langLabel}
          </button>
          <button
            onClick={() => go("#register")}
            data-testid="nav-register-btn"
            className="hidden rounded-full bg-gradient-to-r from-gold-light to-gold-primary px-5 py-2.5 text-sm font-medium text-ink-900 transition-shadow duration-300 hover:shadow-[0_0_24px_rgba(217,119,6,0.55)] sm:block"
          >
            {t.nav.registerNow}
          </button>
          <button
            onClick={() => setOpen((v) => !v)}
            data-testid="nav-mobile-toggle"
            className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-white lg:hidden"
            aria-label="Toggle menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden border-t border-white/10 lg:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col gap-1 px-5 py-4">
              {links.map((l) => (
                <button
                  key={l.href}
                  onClick={() => go(l.href)}
                  className="rounded-lg px-3 py-3 text-left text-base text-white/80 transition-colors hover:bg-white/5 hover:text-gold-light"
                >
                  {l.label}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
